import React, { useLayoutEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { OperationalTension } from './components/home/OperationalTension';
import { WorkflowRows } from './components/home/WorkflowRows';
import { ContextualGrounding } from './components/home/ContextualGrounding';
import { SchoolValue } from './components/home/SchoolValue';
import { CustomerPartnerships } from './components/home/CustomerPartnerships';
import { Proof } from './components/home/Proof';
import { PartnerPathway } from './components/home/PartnerPathway';
import { CallToAction } from './components/CallToAction';
import { Footer } from './components/Footer';
import { Terms } from './pages/Terms';
import { Privacy } from './pages/Privacy';
import { AboutUs } from './pages/AboutUs';
import { PilotClosed } from './pages/PilotClosed';
import { ForSchools } from './pages/ForSchools';
import { Security } from './pages/Security';
import { SubProcessors } from './pages/SubProcessors';
import { ModalProvider } from './components/ModalContext';
import { BackgroundGraphics } from './components/BackgroundGraphics';
import { useAuthRedirect } from './hooks/useAuthRedirect';

// The document has scroll-behavior: smooth, which would animate a route change
// as a long upward scroll through the incoming page. Suspend it for the jump.
const jumpWithoutAnimating = (run: () => void) => {
  const html = document.documentElement;
  const previous = html.style.scrollBehavior;
  html.style.scrollBehavior = 'auto';
  run();
  html.style.scrollBehavior = previous;
};
const ScrollManager = () => {
  const {
    pathname,
    hash
  } = useLocation();
  const navigationType = useNavigationType();
  const previousPathname = useRef(pathname);
  useLayoutEffect(() => {
    const changedPage = previousPathname.current !== pathname;
    previousPathname.current = pathname;
    if (hash) {
      const id = hash.substring(1);
      const scrollToTarget = () => {
        const target = document.getElementById(id);
        if (!target) return;
        if (changedPage) {
          // Arriving from another page: land on the section, no travel.
          jumpWithoutAnimating(() => target.scrollIntoView());
        } else {
          target.scrollIntoView({
            behavior: 'smooth'
          });
        }
      };
      if (document.getElementById(id)) {
        scrollToTarget();
      } else {
        window.setTimeout(scrollToTarget, 80);
      }
      return;
    }

    // Back and forward restore their own position, so leave those alone.
    if (navigationType !== 'POP') {
      jumpWithoutAnimating(() => window.scrollTo(0, 0));
    }
  }, [pathname, hash, navigationType]);
  return null;
};
const AuthRedirectHandler = () => {
  useAuthRedirect();
  return null;
};
const StaticPage = ({
  children
}: {
  children: React.ReactNode;
}) => <>
  <Header isStatic data-id="element-0" data-mp="18cgf7v0805m6" />
  {children}
</>;
const RoutedContent = () => {
  const location = useLocation();
  const shouldReduceMotion = useReducedMotion();
  return <motion.div key={location.pathname} initial={shouldReduceMotion ? false : {
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.24,
    ease: [0.25, 1, 0.5, 1]
  }} data-id="element-1" data-mp="ygd25u3tymkg">
      <Routes location={location} data-id="element-2" data-mp="1r3zgnxeljbm3">
        <Route path="/terms" element={<StaticPage data-id="element-4" data-mp="1r2fwvwtqeifk"><Terms data-id="element-5" data-mp="1w33puo1it9cf" /></StaticPage>} data-id="element-3" data-mp="1r4sj4m6iveyf" />
        <Route path="/privacy" element={<StaticPage data-id="element-7" data-mp="1w2kfpti43p8y"><Privacy data-id="element-8" data-mp="1w0znwbn6mkes" /></StaticPage>} data-id="element-6" data-mp="1w2as2ckjhu6f" />
        <Route path="/about" element={<StaticPage data-id="element-10" data-mp="1i2sxthoh5opo"><AboutUs data-id="element-11" data-mp="1i31elknpxe65" /></StaticPage>} data-id="element-9" data-mp="1i441zilu0yhb" />
        <Route path="/pilot-program" element={<StaticPage data-id="element-13" data-mp="1m44s40iamy47"><PilotClosed data-id="element-14" data-mp="1m3uwnxehbyr4" /></StaticPage>} data-id="element-12" data-mp="1m561m0jnvcjt" />
        <Route path="/schools" element={<StaticPage data-id="element-16" data-mp="6nw43uhcibbz"><ForSchools data-id="element-17" data-mp="6nco4jb99x0l" /></StaticPage>} data-id="element-15" data-mp="1m70q1ou55msn" />
        <Route path="/security" element={<StaticPage data-id="element-19" data-mp="apr856qnv2tg"><Security data-id="element-20" data-mp="aqit7nmmti5h" /></StaticPage>} data-id="element-18" data-mp="6qie00rpl2xd" />
        <Route path="/sub-processors" element={<StaticPage data-id="element-22" data-mp="qx5f5v2uoi8w"><SubProcessors data-id="element-23" data-mp="qwvqqbxgej5b" /></StaticPage>} data-id="element-21" data-mp="arkdmipjfgzv" />
        <Route path="/" element={<>
          <Header data-id="element-25" data-mp="qyyr9pw8fuoa" />
          <main className="relative" data-id="element-26" data-mp="m6p24c2xyyn2">
            <BackgroundGraphics data-id="element-27" data-mp="m5ngxes7ovrm" />
            <div className="relative z-10" data-id="element-28" data-mp="m4m3x4rmxz79">
              <Hero data-id="element-29" data-mp="h6khqaywxjrk" />
              <CustomerPartnerships data-id="element-30" data-mp="h7vnuhftz3lu" />
              <OperationalTension data-id="element-31" data-mp="c89b093wzpdk" />
              <WorkflowRows data-id="element-32" data-mp="c90x8tbmgar6" />
              <ContextualGrounding data-id="element-33" data-mp="c9u2tqyugu7q" />
              <SchoolValue data-id="element-34" data-mp="19vztj2yejgoj" />
              <Proof data-id="element-35" data-mp="19w9o73ue6c0k" />
              <PartnerPathway data-id="element-36" data-mp="19vhqnnh8i47n" />
              <CallToAction data-id="element-37" data-mp="19tnht7j8m2jx" />
            </div>
          </main>
          <Footer data-id="element-38" data-mp="14y83yfl8c6ni" />
        </>} data-id="element-24" data-mp="qxx9z3omiby4" />
      </Routes>
    </motion.div>;
};
export function App() {
  return <BrowserRouter data-id="element-39" data-mp="zwiqktajbgw9">
    <ModalProvider data-id="element-40" data-mp="zw0ohg16y8gf">
      <AuthRedirectHandler data-id="element-41" data-mp="zzoishegmyw7" />
      <ScrollManager data-id="element-42" data-mp="vwruqb65iakv" />
      <div className="w-full min-h-screen bg-white text-gray-800 font-sans" data-id="element-43" data-mp="vvgps80yyws6">
        <RoutedContent data-id="element-44" data-mp="3kob5kj63uc7e" />
      </div>
    </ModalProvider>
  </BrowserRouter>;
}